import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import * as colors from 'src/styles/colors';
import { mobilePoint, modalZIndex } from 'src/styles/globalStyle';
import { FaAngleLeft, FaAngleRight, FaTimes } from 'react-icons/fa';

const WindowMask = styled.div<{ isOpen: boolean }>`
  display: ${(prop) => (prop.isOpen ? 'flex' : 'none')};
  position: fixed;
  z-index: ${modalZIndex};
  align-items: center;
  justify-items: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(39, 38, 38, 0.7);
`;

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  text-align: center;
  position: relative;
  color: ${colors.white};
  padding-top: 3em;
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 1em;
  right: 1em;
  font-size: 2em;
  cursor: pointer;
`;

const ZoomImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ActionIcon = styled.span`
  cursor: pointer;
  color: #ffffff;
  font-size: 2rem;
`;

const ZoomImageWrapper = styled.div`
  display: block;
  text-align: center;
  width: max-content;
`;

const ZoomImage = styled.img`
  display: inline-block;
  height: 20rem;
  max-width: 100%;
  margin-left: 3em;
  margin-right: 3em;

  @media (max-width: ${mobilePoint}) {
    height: 15em;
    max-width: unset;
    margin-left: 1em;
    margin-right: 1em;
  }
`;

const ImageList = styled.div`
  display: inline-flex;
  margin-top: 1rem;

  @media (max-width: ${mobilePoint}) {
    display: flex;
    margin-left: 10vw;
    overflow: auto;
  }
`;

const ImageListItem = styled.img<{ isActive?: boolean }>`
  cursor: pointer;
  display: inline-block;
  width: 6rem;
  max-height: 6rem;
  margin-right: 1.6rem;
  border: ${(props) => props.isActive && 'solid 2px ' + colors.primaryBlue_500};

  &:last-child {
    margin-right: 0;
  }
`;

const CarouselIndex = styled.div`
  display: flex;
  justify-content: center;
`;

const Form = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: 1fr 3fr;
  font-size: 15px;
  width: 20em;
  justify-items: baseline;
  line-height: 38px;
  div {
    border-bottom: 1px solid ${colors.BlueGray_200};
    :nth-child(2n-1) {
      border-right: 1px solid ${colors.BlueGray_200};
    }
    :nth-last-child(1) {
      border-bottom: none;
    }
    :nth-last-child(2) {
      border-bottom: none;
    }
    div {
      border: none;
      :nth-child(2n-1) {
        border: none;
      }
    }
  }
`;
const Title = styled.div`
  display: flex;
  width: 100%;
`;
const Content = styled.div`
  display: flex;
  width: 100%;
  font-family: NotoSansTC-Bold;
  justify-items: baseline;
  padding-left: 1em;
`;

const NoContent = styled.div`
  color: ${colors.BlueGray_200};
`;

export type DataType = {
  /** 圖樣中文。*/
  tmarkDraftC?: string;
  /** 圖樣英文。*/
  tmarkDraftE?: string;
  /** 圖樣日文。*/
  tmarkDraftJ?: string;
  /** 圖樣記號。*/
  tmarkSign?: string;
};

interface IProps {
  isOpen: boolean;
  setClose: () => void;
  imagesUrls: string[];
  activeImageIdx: number;
  data?: DataType;
}

const ZoomImages: React.FC<IProps> = ({ isOpen, setClose, imagesUrls, activeImageIdx, data }) => {
  const [currentImage, setCurrentImage] = useState<string>(imagesUrls[0]);
  const [currentImageIdx, setCurrentImageIdx] = useState<number>(0);
  const NO_CONTENT = <NoContent>(無)</NoContent>; //該欄位無資料顯示項目

  useEffect(() => {
    setCurrentImageIdx(activeImageIdx);
  }, [activeImageIdx]);

  useEffect(() => {
    const currentImageUrl = imagesUrls.find((_url, index) => index === currentImageIdx);
    setCurrentImage(currentImageUrl ? currentImageUrl : imagesUrls[0]);
  }, [currentImageIdx, imagesUrls]);

  return (
    <WindowMask isOpen={isOpen}>
      <Wrapper>
        <CloseIcon>
          <FaTimes onClick={setClose} />
        </CloseIcon>
        <ZoomImageContainer>
          {imagesUrls.length > 1 && (
            <ActionIcon>
              <FaAngleLeft
                className="icon-slide-left"
                onClick={() => {
                  if (currentImageIdx === 0) return;
                  setCurrentImageIdx(currentImageIdx - 1);
                }}
              />
            </ActionIcon>
          )}
          <ZoomImageWrapper>
            <ZoomImage src={currentImage} alt="" />
          </ZoomImageWrapper>
          {imagesUrls.length > 1 && (
            <ActionIcon>
              <FaAngleRight
                onClick={() => {
                  if (currentImageIdx >= imagesUrls.length - 1) return;
                  setCurrentImageIdx(currentImageIdx + 1);
                }}
              />
            </ActionIcon>
          )}
        </ZoomImageContainer>

        {imagesUrls.length > 1 && (
          <>
            <ImageList onClick={(e) => e.stopPropagation()}>
              {imagesUrls.map((url, idx) => (
                <ImageListItem
                  key={idx}
                  src={url}
                  alt=""
                  isActive={currentImageIdx === idx}
                  onClick={() => {
                    setCurrentImageIdx(idx);
                  }}
                />
              ))}
            </ImageList>
          </>
        )}
        {data && (
          <CarouselIndex>
            <Form>
              <Title>圖樣中文</Title>
              <Content>{data.tmarkDraftC || NO_CONTENT}</Content>
              <Title>圖樣英文</Title>
              <Content>{data.tmarkDraftE || NO_CONTENT}</Content>
              <Title>圖樣日文</Title>
              <Content>{data.tmarkDraftJ || NO_CONTENT}</Content>
              <Title>圖樣記號</Title>
              <Content>{data.tmarkSign || NO_CONTENT}</Content>
            </Form>
          </CarouselIndex>
        )}
      </Wrapper>
    </WindowMask>
  );
};

export default ZoomImages;
